// PLM: change orders from CAD. Import a CAD BOM export, see it matched to BC items and compared with the
// production BOMs in BC, fill in what new items need, approve, and release it to BC.
import { Fragment, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, ApiError, type EcoDetail, type EcoPart } from "../api";
import { useLoad } from "../hooks";
import { CommandBar } from "../Shell";
import { ErrorBox, ago, label } from "../ui";
import { XSection, pct } from "../crm";
import { bcUrl } from "../config";
import { ROLES, findHeader, guessMapping, readTable, toRows, topFromFileName, type Mapping, type Table } from "../cadfile";

const usd = (v: number | null | undefined, d = 2) => (v === null || v === undefined ? "" : v.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d }));
const qty = (v: number | null | undefined) => (v === null || v === undefined ? "" : v.toLocaleString("en-US", { maximumFractionDigits: 5 }));
const usDate = (d: string | null | undefined) => (d ? new Date(d.length === 10 ? d + "T00:00:00" : d).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) : "");
const ACT = (p: string) => <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d={p} fill="none" stroke="currentColor" strokeWidth="1.4" /></svg>;
const ICON = {
  plus: ACT("M8 3v10M3 8h10"), up: ACT("M8 13V3M4 7l4-4 4 4"), next: ACT("M3 8h9M9 4l4 4-4 4"), ok: ACT("M3 8.5l3 3 7-7"), back: ACT("M13 8H4M7 4L3 8l4 4"),
  x: ACT("M4 4l8 8M12 4l-8 8"), sync: ACT("M13 5A5.5 5.5 0 0 0 3 6M3 11a5.5 5.5 0 0 0 10-1M13 2v3h-3M3 14v-3h3"), send: ACT("M2 8l12-5-5 12-2-5z"), excel: ACT("M2.5 2.5h11v11h-11zM2.5 6.5h11M6.5 2.5v11"),
};
const WarnIcon = () => <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5l7 13H1z" fill="#BC4B09" /><path d="M8 6v4M8 11.5v1" stroke="#fff" strokeWidth="1.4" /></svg>;
const InfoIcon = () => <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="7" fill="#0F6C77" /><path d="M8 7v4.5M8 4.5v1" stroke="#fff" strokeWidth="1.4" /></svg>;

export const ECO_STATES = [{ id: "draft", name: "Draft" }, { id: "in_review", name: "In Review" }, { id: "approved", name: "Approved" }, { id: "released", name: "Released" }];
const STATE_NAME: Record<string, string> = { draft: "Draft", in_review: "In Review", approved: "Approved", released: "Released", cancelled: "Cancelled" };
const STATE_TONE: Record<string, string> = { draft: "", in_review: "warn", approved: "info", released: "ok", cancelled: "" };
const CAD_SYSTEMS = ["SOLIDWORKS", "Autodesk Inventor", "Onshape", "Autodesk Fusion", "Solid Edge", "PTC Creo", "Siemens NX", "SOLIDWORKS PDM", "Autodesk Vault", "Other"];
const bcItem = (no: string) => `${bcUrl(30)}&filter=${encodeURIComponent(`'No.' IS '${no}'`)}`;
const bcBom = (no: string) => `${bcUrl(99000786)}&filter=${encodeURIComponent(`'No.' IS '${no}'`)}`;

/* ---------- Change Orders list ---------- */

export function ChangeOrdersPage() {
  const [params, setParams] = useSearchParams();
  const view = params.get("view") ?? "open";
  const { data, error } = useLoad(() => api.ecos(), []);
  const all = data ?? [];
  const count = (s: string) => all.filter((e) => e.state === s).length;
  const month = new Date().toISOString().slice(0, 7);
  const rows = view === "open" ? all.filter((e) => ["draft", "in_review", "approved"].includes(e.state)) : view === "all" ? all : all.filter((e) => e.state === view);
  const set = (v: string | null) => { const p = new URLSearchParams(params); v ? p.set("view", v) : p.delete("view"); setParams(p, { replace: true }); };
  return (
    <>
      <CommandBar status={data ? `${rows.length} ${rows.length === 1 ? "change order" : "change orders"}` : ""}>
        <Link className="cmd" to="/plm/import">{ICON.up}Import from CAD</Link>
      </CommandBar>
      <div className="page card-page">
        <div className="rec-title"><h1>Change Orders</h1></div>
        <ErrorBox error={error} />
        <div className="cues">
          <button className="cue as-btn" onClick={() => set("draft")}><span className="cue-l">Draft</span><span className="cue-v">{count("draft")}</span></button>
          <button className="cue as-btn" onClick={() => set("in_review")}><span className="cue-l">Waiting for Approval</span><span className="cue-v">{count("in_review")}</span></button>
          <button className="cue as-btn" onClick={() => set("approved")}><span className="cue-l">Approved, Not Released</span><span className="cue-v">{count("approved")}</span></button>
          <button className="cue as-btn" onClick={() => set("released")}><span className="cue-l">Released This Month</span><span className="cue-v">{all.filter((e) => e.releasedAt?.startsWith(month)).length}</span></button>
          <div className="cue"><span className="cue-l">New Items in Open Changes</span><span className="cue-v">{all.filter((e) => ["draft", "in_review", "approved"].includes(e.state)).reduce((t, e) => t + e.newItems, 0)}</span></div>
        </div>
        <div className="panel">
          <div role="tablist" aria-label="Views" className="views">
            {[["open", "Open"], ["draft", "Draft"], ["in_review", "In Review"], ["approved", "Approved"], ["released", "Released"], ["all", "All"]].map(([id, name]) => (
              <button key={id} role="tab" aria-selected={view === id} className={view === id ? "on" : ""} onClick={() => set(id === "open" ? null : id)}>{name}</button>
            ))}
          </div>
          {!data ? <div className="empty">Loading…</div> : !rows.length ? (
            <div className="empty">No change orders in this view. <Link to="/plm/import">Import a BOM from CAD</Link> to start one.</div>
          ) : (
            <div className="table-wrap">
              <table className="dgrid num-grid">
                <thead><tr><th>No.</th><th>Description</th><th>Top Assembly</th><th>Source</th><th className="r">Parts</th><th className="r">New Items</th><th className="r">Assemblies</th><th>Status</th><th>Created By</th><th>Updated</th></tr></thead>
                <tbody>{rows.map((e) => (
                  <tr key={e.id}>
                    <td><Link to={`/plm/changes/${e.number}`}>{e.number}</Link></td>
                    <td>{e.title}</td>
                    <td className="mono">{e.topPart}</td>
                    <td>{[e.cadSystem, e.fileName].filter(Boolean).join(", ")}</td>
                    <td className="r">{e.parts}</td>
                    <td className="r">{e.newItems || ""}</td>
                    <td className="r">{e.assemblies}</td>
                    <td><span className={`status ${STATE_TONE[e.state] ?? ""}`}>{STATE_NAME[e.state] ?? label(e.state)}</span></td>
                    <td>{e.createdByName ?? ""}</td>
                    <td className="muted">{ago(e.updatedAt)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ---------- Import from CAD ---------- */

export function ImportCadPage() {
  const nav = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [table, setTable] = useState<Table | null>(null);
  const [headerAt, setHeaderAt] = useState(0);
  const [map, setMap] = useState<Mapping>({});
  const [search] = useSearchParams();
  const [top, setTop] = useState({ partNumber: search.get("part") ?? "", description: search.get("description") ?? "" }); // prefilled from an item setup
  const [form, setForm] = useState({ cadSystem: "SOLIDWORKS", defaultUom: "PCS", reason: "" });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);

  const load = async (f: File) => {
    setError(null);
    try {
      const t = await readTable(f);
      if (!t.length) throw new Error("That file is empty.");
      const h = findHeader(t);
      setFile(f); setTable(t); setHeaderAt(h); setMap(guessMapping(t[h])); setTop(topFromFileName(f.name));
      if (/\.ipt|\.iam|inventor/i.test(f.name)) setForm((x) => ({ ...x, cadSystem: "Autodesk Inventor" }));
    } catch (e) { setError(e); setTable(null); }
  };
  const header = table?.[headerAt] ?? [];
  const parsed = useMemo(() => (table && map.partNumber !== undefined ? toRows(table, headerAt, map) : null), [table, headerAt, map]);
  const levels = parsed ? Math.max(1, ...parsed.rows.map((r) => r.level)) : 0;
  const distinct = parsed ? new Set(parsed.rows.map((r) => r.partNumber)).size : 0;
  const missing = ROLES.filter((r) => r.required && map[r.id] === undefined);

  const submit = async () => {
    if (!parsed) return;
    setBusy(true); setError(null);
    try {
      const r = await api.importCad({ top, cadSystem: form.cadSystem, fileName: file?.name, defaultUom: form.defaultUom, reason: form.reason, rows: parsed.rows });
      nav(`/plm/changes/${r.number}`, { state: { warnings: r.warnings, matchError: r.matchError } });
    } catch (e) { setError(e); } finally { setBusy(false); }
  };

  return (
    <>
      <CommandBar><Link className="cmd" to="/plm/changes">{ICON.back}Change Orders</Link></CommandBar>
      <div className="page card-page">
        <div className="rec-title">
          <Link to="/plm/changes" className="crumb">Change Orders</Link>
          <div className="line"><h1>Import from CAD</h1></div>
          <span className="meta">A BOM export becomes a change order: every part is matched to a Business Central item and every assembly is compared with its production BOM before anything changes in BC.</span>
        </div>
        <ErrorBox error={error} />

        <section className="panel pad">
          <h2 className="ph">1. BOM file</h2>
          <div className={`dropzone ${drag ? "on" : ""}`} onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f) load(f); }}>
            <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv,.txt,.tsv" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) load(f); e.target.value = ""; }} />
            {file ? <span><strong>{file.name}</strong>, {table?.length ?? 0} rows read. <button className="linkish" onClick={() => fileRef.current?.click()}>Choose another file</button></span>
              : <span>Drop an Excel (.xlsx) or CSV BOM export here, or <button className="linkish" onClick={() => fileRef.current?.click()}>choose a file</button>.</span>}
          </div>
          <details className="howto">
            <summary>How to export a BOM from your CAD system</summary>
            <ul>
              <li><strong>SOLIDWORKS:</strong> insert a Bill of Materials on the assembly drawing (or Insert › Tables in the assembly) with the type <em>Indented</em>, right-click it, Save As, Excel or CSV.</li>
              <li><strong>Inventor:</strong> Assemble › Bill of Materials › Structured tab, set to all levels, Export Bill of Materials as Excel.</li>
              <li><strong>Onshape:</strong> open the assembly's Bill of Materials, set it to Structured, Export as CSV or Excel.</li>
              <li><strong>Other systems and PDM:</strong> any export with a part number and quantity per row. Levels come from a Level column or from item numbers such as 1, 1.1, 1.1.2.</li>
            </ul>
          </details>
        </section>

        {table && (
          <section className="panel pad">
            <h2 className="ph">2. Top assembly and columns</h2>
            <div className="form-grid">
              <div className="field"><label htmlFor="tp">Top Assembly No.</label><input id="tp" className="input" maxLength={20} value={top.partNumber} onChange={(e) => setTop({ ...top, partNumber: e.target.value })} required /></div>
              <div className="field"><label htmlFor="td">Description</label><input id="td" className="input" maxLength={100} value={top.description} onChange={(e) => setTop({ ...top, description: e.target.value })} /></div>
              <div className="field"><label htmlFor="cs">CAD System</label><select id="cs" className="select" value={form.cadSystem} onChange={(e) => setForm({ ...form, cadSystem: e.target.value })}>{CAD_SYSTEMS.map((s) => <option key={s}>{s}</option>)}</select></div>
              <div className="field"><label htmlFor="du">Unit of Measure for New Items</label><input id="du" className="input" maxLength={10} value={form.defaultUom} onChange={(e) => setForm({ ...form, defaultUom: e.target.value.toUpperCase() })} /></div>
              <div className="field"><label htmlFor="hr">Header Row</label><select id="hr" className="select" value={headerAt} onChange={(e) => { const h = Number(e.target.value); setHeaderAt(h); setMap(guessMapping(table[h])); }}>
                {table.slice(0, 15).map((r, i) => <option key={i} value={i}>Row {i + 1}: {r.filter(Boolean).slice(0, 4).join(", ").slice(0, 60)}</option>)}</select></div>
            </div>
            <div className="field"><label htmlFor="rs">Reason for Change</label><textarea id="rs" className="textarea" placeholder="e.g. Rev B: aluminum frame weldment, 32-spoke front wheel, adds kickstand" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
            <table className="dgrid compact map-grid">
              <thead><tr><th>Field</th><th>Column in the File</th><th>First Value</th></tr></thead>
              <tbody>{ROLES.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}{r.required ? " *" : ""}</td>
                  <td><select className="select" aria-label={`${r.name} column`} value={map[r.id] ?? ""} onChange={(e) => setMap({ ...map, [r.id]: e.target.value === "" ? undefined : Number(e.target.value) })}>
                    <option value="">{r.required ? "Choose…" : "Not in this file"}</option>
                    {header.map((h, i) => <option key={i} value={i}>{h || `Column ${i + 1}`}</option>)}
                  </select></td>
                  <td className="muted mono">{map[r.id] !== undefined ? table[headerAt + 1]?.[map[r.id]!] ?? "" : ""}</td>
                </tr>
              ))}</tbody>
            </table>
          </section>
        )}

        {parsed && (
          <section className="panel pad">
            <h2 className="ph">3. Preview</h2>
            <div className="xsec-chips" style={{ marginBottom: 8 }}>
              <span className="xchip">{parsed.rows.length} rows</span><span className="xchip">{distinct} distinct parts</span><span className="xchip">{levels} {levels === 1 ? "level" : "levels"}</span>
              {parsed.skipped.length > 0 && <span className="xchip">{parsed.skipped.length} blank rows skipped</span>}
            </div>
            {levels === 1 && map.position === undefined && map.level === undefined && <div className="notify info" role="status"><InfoIcon /><span>No level or item number column, so every part goes directly under {top.partNumber || "the top assembly"}. Export an indented BOM to bring in sub-assemblies.</span></div>}
            <div className="table-wrap" style={{ maxHeight: 420 }}>
              <table className="dgrid num-grid">
                <thead><tr><th>Item</th><th>Part No.</th><th>Description</th><th className="r">Qty. per</th><th>Revision</th><th>Material</th></tr></thead>
                <tbody>
                  <tr className="tree-top"><td /><td className="mono">{top.partNumber}</td><td>{top.description}</td><td /><td /><td /></tr>
                  {parsed.rows.slice(0, 400).map((r, i) => (
                    <tr key={i}><td className="muted">{r.position ?? ""}</td><td className="mono" style={{ paddingLeft: 8 + r.level * 18 }}>{r.partNumber}</td><td>{r.description ?? ""}</td><td className="r">{qty(r.quantity)}</td><td>{r.revision ?? ""}</td><td>{r.material ?? ""}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            {parsed.rows.length > 400 && <p className="muted">Showing the first 400 of {parsed.rows.length} rows.</p>}
            <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center" }}>
              <button className="btn primary" disabled={busy || !top.partNumber.trim() || missing.length > 0 || !parsed.rows.length} onClick={submit}>{busy ? "Creating and matching to BC…" : "Create Change Order"}</button>
              {missing.length > 0 && <span className="muted">Choose the {missing.map((m) => m.name).join(" and ")} column.</span>}
              <span className="muted">Nothing changes in Business Central until the change order is approved and released.</span>
            </div>
          </section>
        )}
      </div>
    </>
  );
}

/* ---------- Change Order card ---------- */

export function ChangeOrderPage() {
  const { id = "" } = useParams();
  const { data, error, reload } = useLoad(() => api.eco(id), [id]);
  const [over, setOver] = useState<EcoDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [showSame, setShowSame] = useState(false);
  const arrived = (useLocation().state ?? {}) as { warnings?: string[]; matchError?: string | null };
  const e = over && data && over.id === data.id && over.updatedAt >= data.updatedAt ? over : data;

  const run = async (fn: () => Promise<EcoDetail | void>) => {
    setBusy(true); setActionError(null);
    try { const r = await fn(); if (r) setOver(r); else { setOver(null); await reload(); } } catch (err) { setActionError(err); await reload(); setOver(null); } finally { setBusy(false); setProgress(null); }
  };
  const release = () => run(async () => {
    for (let i = 0; i < 50; i++) {
      const r = await api.releaseEco(e!.id);
      if (r.done) return r.eco!;
      if (r.progress) setProgress(`Releasing to Business Central… ${r.progress.done} of ${r.progress.total} changes`);
    }
  });

  if (error) return <div className="page"><ErrorBox error={error} /></div>;
  if (!e) return <div className="page"><div className="panel empty">Loading…</div></div>;

  const idx = ECO_STATES.findIndex((s) => s.id === e.state);
  const partOf = new Map(e.parts.map((p) => [p.partNumber, p]));
  const newParts = e.parts.filter((p) => !p.inBc);
  const changed = e.compare.filter((c) => c.status !== "unchanged");
  const lineChanges = e.compare.reduce((t, c) => t + c.added + c.removed + c.changed, 0);
  const missingSvc = e.bcCheck ? e.services.filter((s) => !e.bcCheck!.services[s.name]) : [];
  const lastLog = e.releaseLog.at(-1);
  const editable = e.state === "draft";
  const delta = e.cost.rolled !== null && e.cost.current !== null ? e.cost.rolled - e.cost.current : null;
  const csv = () => {
    const esc = (v: unknown) => { const t = v === null || v === undefined ? "" : String(v); return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t; };
    const body = e.compare.flatMap((c) => c.rows.map((r) => [c.bomNo, c.item, c.description, r.change, r.no, r.description, r.was ?? "", r.now ?? ""]));
    const head = ["Production BOM No.", "Assembly", "Assembly Description", "Change", "No.", "Description", "Qty. per in BC", "Qty. per in CAD"];
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([[head, ...body].map((r) => r.map(esc).join(",")).join("\r\n")], { type: "text/csv" })); a.download = `${e.number}.csv`; a.click();
  };

  return (
    <>
      <CommandBar status={progress ?? `Updated ${ago(e.updatedAt)}`}>
        {(e.state === "draft" || e.state === "in_review") && <button className="cmd" disabled={busy} onClick={() => run(() => api.matchEco(e.id))}>{ICON.sync}Match to Business Central</button>}
        {e.state === "draft" && <button className="cmd" disabled={busy} onClick={() => run(() => api.moveEco(e.id, "submit"))}>{ICON.send}Send for Approval</button>}
        {e.state === "in_review" && <button className="cmd" disabled={busy} onClick={() => run(() => api.moveEco(e.id, "approve"))}>{ICON.ok}Approve</button>}
        {(e.state === "in_review" || e.state === "approved") && <button className="cmd" disabled={busy} onClick={() => { const r = window.prompt("Why is it going back to draft?") ?? undefined; if (r !== undefined) run(() => api.moveEco(e.id, "return", r)); }}>{ICON.back}Send Back to Draft</button>}
        {e.state === "approved" && <button className="cmd" disabled={busy} onClick={release}>{ICON.next}{e.releaseProgress ? "Continue Release to BC" : "Release to Business Central"}</button>}
        {["draft", "in_review", "approved"].includes(e.state) && <button className="cmd" disabled={busy} onClick={() => { if (window.confirm(`Cancel ${e.number}? Nothing has changed in Business Central${e.releaseProgress?.done ? " except what the release log lists" : ""}.`)) run(() => api.moveEco(e.id, "cancel")); }}>{ICON.x}Cancel Change</button>}
        <span className="cmd-sep" aria-hidden="true" />
        <button className="cmd" onClick={csv}>{ICON.excel}Open in Excel</button>
        <a className="cmd" href={bcItem(e.topItem)} target="_blank" rel="noopener noreferrer">Item {e.topItem} in BC ↗</a>
      </CommandBar>
      <div className="page card-page">
        <div className="rec-title">
          <Link to="/plm/changes" className="crumb">Change Orders</Link>
          <div className="line">
            <h1>{e.number} · {e.title}</h1>
            <span className="meta">Status: {STATE_NAME[e.state] ?? label(e.state)}</span>
            <span className="meta">Top Assembly: {e.topItem}</span>
            {e.cadSystem && <span className="meta">From {e.cadSystem}</span>}
          </div>
          {e.state !== "cancelled" && (
            <div className="stage-steps" aria-label="Stages">
              {ECO_STATES.map((s, i) => <span key={s.id} className={`st ${i < idx ? "done" : i === idx ? "cur" : ""}`}>{i < idx || e.state === "released" ? "✓ " : ""}{s.name}</span>)}
            </div>
          )}
        </div>

        {e.bcCheck && !e.bcCheck.ready && e.state !== "released" && (
          <div className="notify warn" role="status">
            <WarnIcon /><span>To compare with and update production BOMs, publish {missingSvc.map((s) => `page ${s.page} (${s.caption}) as ${s.name}`).join(", ")} on Business Central's Web Services page, then choose Match to Business Central.</span>
            <a href={bcUrl(810)} target="_blank" rel="noopener noreferrer">Open Web Services in BC</a>
          </div>
        )}
        {arrived.matchError && !e.matchedAt && <div className="notify warn" role="alert"><WarnIcon /><span>Imported, but matching to Business Central failed: {arrived.matchError}</span></div>}
        {arrived.warnings?.map((w) => <div key={w} className="notify warn" role="status"><WarnIcon /><span>{w}</span></div>)}
        {!e.matchedAt && <div className="notify info" role="status"><InfoIcon /><span>Not matched to Business Central yet.</span><button className="linkish" disabled={busy} onClick={() => run(() => api.matchEco(e.id))}>Match now</button></div>}
        {editable && newParts.length > 0 && <div className="notify info" role="status"><InfoIcon /><span>{newParts.length} {newParts.length === 1 ? "part isn't" : "parts aren't"} in Business Central and will be created as {newParts.length === 1 ? "an item" : "items"} on release. Check their numbers and descriptions under New Items, or map them to existing items.</span></div>}
        {e.cost.missing.length > 0 && e.state !== "released" && <div className="notify warn" role="status"><WarnIcon /><span>{e.cost.missing.length} {e.cost.missing.length === 1 ? "part has" : "parts have"} no cost ({e.cost.missing.slice(0, 5).join(", ")}{e.cost.missing.length > 5 ? "…" : ""}), so the rolled-up cost is low. Enter estimates under New Items.</span></div>}
        {e.state === "approved" && lastLog && !lastLog.ok && <div className="notify warn" role="alert"><WarnIcon /><span>The release stopped: {lastLog.step}. {lastLog.detail}. {e.releaseProgress ? `${e.releaseProgress.done} of ${e.releaseProgress.total} changes are in BC.` : ""} Fix it in BC if needed, then continue the release.</span></div>}
        {e.state === "released" && <div className="notify info" role="status"><InfoIcon /><span>Released to Business Central on {usDate(e.releasedAt)}. {changed.length} production {changed.length === 1 ? "BOM" : "BOMs"} certified.</span><a href={bcBom(e.compare[0]?.bomNo ?? e.topItem)} target="_blank" rel="noopener noreferrer">Open the production BOM in BC</a></div>}
        <ErrorBox error={actionError instanceof ApiError && actionError.body?.code === "services_missing" ? null : actionError} />

        <div className="cues">
          <div className="cue"><span className="cue-l">Parts</span><span className="cue-v">{e.parts.length}</span></div>
          <div className="cue"><span className="cue-l">New Items</span><span className="cue-v">{newParts.length}</span></div>
          <div className="cue"><span className="cue-l">Production BOMs Changing</span><span className="cue-v">{changed.length} of {e.compare.length}</span></div>
          <div className="cue"><span className="cue-l">BOM Line Changes</span><span className="cue-v">{lineChanges}</span></div>
          <div className="cue"><span className="cue-l">Rolled-up Material Cost</span><span className="cue-v">{usd(e.cost.rolled)}</span>{delta !== null && <span className={`cue-n ${delta > 0 ? "bad" : "good"}`}>{delta > 0 ? "+" : ""}{usd(delta)} vs. BC unit cost</span>}</div>
        </div>

        <div className="card-cols">
          <div className="tabs">
            <XSection title="General" chips={[e.cadSystem, e.fileName]}>
              <GeneralTab e={e} editable={e.state === "draft" || e.state === "in_review"} busy={busy} save={(b) => run(() => api.updateEco(e.id, b))} />
            </XSection>

            <XSection title={e.state === "released" ? "Changes Made to Production BOMs" : "Changes to Production BOMs"} chips={[`${changed.length} ${e.state === "released" ? "changed" : "changing"}`, `${lineChanges} line ${lineChanges === 1 ? "change" : "changes"}`]}
              actions={<label className="inline-check"><input type="checkbox" checked={showSame} onChange={(x) => setShowSame(x.target.checked)} /> Show unchanged lines</label>}>
              {e.compare.map((c) => (
                <div key={c.partNumber} className="bom-diff">
                  <div className="bom-diff-h">
                    <span className="mono">{c.bomNo}</span><span>{c.description}</span>
                    <span className={`status ${c.status === "changed" ? "warn" : c.status === "new" || c.status === "no_bom" ? "info" : ""}`}>{c.status === "changed" ? "Changed" : c.status === "new" ? "New item and BOM" : c.status === "no_bom" ? "New production BOM" : "No change"}</span>
                    <span className="muted">{[c.added && `${c.added} added`, c.removed && `${c.removed} removed`, c.changed && `${c.changed} quantity`].filter(Boolean).join(", ")}</span>
                  </div>
                  {(c.status !== "unchanged" || showSame) && (
                    <table className="dgrid compact num-grid">
                      <thead><tr><th style={{ width: 110 }}>Change</th><th>No.</th><th>Description</th><th className="r">Qty. per in BC</th><th className="r">Qty. per in CAD</th></tr></thead>
                      <tbody>{c.rows.filter((r) => showSame || r.change !== "same").map((r) => (
                        <tr key={r.no + r.change} className={`chg-${r.change}`}>
                          <td><span className={`chg ${r.change}`}>{r.change === "added" ? "Added" : r.change === "removed" ? "Removed" : r.change === "quantity" ? "Quantity" : "Same"}</span></td>
                          <td className="mono">{r.no}{r.childPart && !partOf.get(r.childPart)?.inBc ? <span className="muted"> new</span> : ""}</td>
                          <td>{r.description}</td><td className="r">{qty(r.was)}</td><td className="r">{qty(r.now)}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  )}
                </div>
              ))}
            </XSection>

            <XSection title="Structure" chips={[`${e.parts.length} parts`, `${e.compare.length} assemblies`]} open={false}>
              <StructureTree e={e} />
            </XSection>

            <XSection title="New Items" chips={[newParts.length ? `${newParts.length} to create in BC` : "none"]} open={editable && newParts.length > 0}>
              {newParts.length ? <NewItems e={e} parts={newParts} editable={editable} busy={busy} save={(p, b) => run(() => api.updateEcoPart(e.id, p.id, b))} />
                : <p className="dash-empty" style={{ margin: 0 }}>{e.state === "released" ? `Items created by this change: ${e.releaseLog.filter((l) => l.ok && l.step.startsWith("Created item")).map((l) => l.step.split(" ")[2]).join(", ") || "none"}.` : "Every part is already an item in Business Central."}</p>}
            </XSection>

            <XSection title="Release Log" chips={[e.releaseLog.length ? `${e.releaseLog.length} entries` : "not released", e.releaseProgress ? `${e.releaseProgress.done} of ${e.releaseProgress.total} changes` : null]} open={e.state === "approved" || e.state === "released"}>
              {e.releaseLog.length ? (
                <table className="dgrid compact"><thead><tr><th>When</th><th>Step</th><th>Result</th></tr></thead>
                  <tbody>{e.releaseLog.map((l, i) => <tr key={i}><td className="muted" style={{ whiteSpace: "nowrap" }}>{new Date(l.at).toLocaleString("en-US", { month: "2-digit", day: "2-digit", hour: "numeric", minute: "2-digit" })}</td><td>{l.step}</td><td>{l.ok ? <span className="status ok">Done</span> : <span className="status danger" title={l.detail}>Failed: {l.detail}</span>}</td></tr>)}</tbody></table>
              ) : <p className="dash-empty" style={{ margin: 0 }}>Release creates the new items, updates each production BOM line by line, certifies it, and sets the assembly items to be made from it. Each step is listed here.</p>}
            </XSection>

            <XSection title="Approval History" chips={[`${e.history.length} ${e.history.length === 1 ? "entry" : "entries"}`]} open={false}>
              <table className="dgrid compact"><thead><tr><th>Status</th><th>When</th><th>By</th><th>Note</th></tr></thead>
                <tbody>{e.history.map((h, i) => <tr key={i}><td>{STATE_NAME[h.toState] ?? label(h.toState)}</td><td>{usDate(h.at)}</td><td>{h.actorName ?? ""}</td><td>{h.reason ?? ""}</td></tr>)}</tbody></table>
            </XSection>
          </div>

          <aside className="factboxes" aria-label="FactBoxes">
            <section className="factbox">
              <h2>Cost Impact</h2>
              <dl>
                <dt>Rolled-up Material Cost</dt><dd>{usd(e.cost.rolled)}</dd>
                <dt>Unit Cost in BC</dt><dd>{e.cost.current === null ? "New item" : usd(e.cost.current)}</dd>
                <dt>Difference</dt><dd className={delta !== null && delta > 0 ? "bad" : ""}>{delta === null ? "" : `${delta > 0 ? "+" : ""}${usd(delta)}`}</dd>
                <dt>Avg. Price, Last 12 Mo.</dt><dd>{e.cost.avgPrice === null ? "No sales" : usd(e.cost.avgPrice)}</dd>
                <dt>Margin Now</dt><dd>{pct(e.cost.marginNow)}</dd>
                <dt>Margin After Change</dt><dd className={e.cost.marginNew !== null && e.cost.marginNow !== null && e.cost.marginNew < e.cost.marginNow ? "bad" : ""}>{pct(e.cost.marginNew)}</dd>
              </dl>
              <p className="empty-note">Material only, from BC unit costs and your estimates for new parts. Labor and overhead come from the routing.</p>
            </section>
            <section className="factbox">
              <h2>Business Central</h2>
              <dl>
                <dt>Matched</dt><dd>{e.matchedAt ? ago(e.matchedAt) : "Not yet"}</dd>
                {e.services.map((s) => <Fragment key={s.name}><dt>{s.name}</dt><dd>{!e.bcCheck ? "" : e.bcCheck.services[s.name] ? "Published" : <span className="bad">Not published</span>}</dd></Fragment>)}
                <dt>Top Item</dt><dd><a href={bcItem(e.topItem)} target="_blank" rel="noopener noreferrer">{e.topItem} ↗</a></dd>
              </dl>
            </section>
          </aside>
        </div>
      </div>
    </>
  );
}

function GeneralTab({ e, editable, busy, save }: { e: EcoDetail; editable: boolean; busy: boolean; save: (b: { title?: string; reason?: string; effectiveDate?: string | null }) => void }) {
  const [v, setV] = useState({ title: e.title, reason: e.reason, effectiveDate: e.effectiveDate ?? "" });
  const dirty = v.title !== e.title || v.reason !== e.reason || v.effectiveDate !== (e.effectiveDate ?? "");
  return (
    <div className="bc-fields">
      <dl>
        <dt>No.</dt><dd>{e.number}</dd>
        <dt>Description</dt><dd>{editable ? <input className="input" aria-label="Description" value={v.title} onChange={(x) => setV({ ...v, title: x.target.value })} /> : e.title}</dd>
        <dt>Reason for Change</dt><dd>{editable ? <textarea className="textarea" aria-label="Reason for change" value={v.reason} onChange={(x) => setV({ ...v, reason: x.target.value })} /> : e.reason}</dd>
        <dt>Effective Date</dt><dd>{editable ? <input type="date" className="input" aria-label="Effective date" value={v.effectiveDate} onChange={(x) => setV({ ...v, effectiveDate: x.target.value })} /> : usDate(e.effectiveDate)}</dd>
        {editable && dirty && <><dt /><dd><button className="btn primary" disabled={busy} onClick={() => save({ ...v, effectiveDate: v.effectiveDate || null })}>Save</button></dd></>}
      </dl>
      <dl>
        <dt>Top Assembly</dt><dd className="mono">{e.topItem}{e.topItem !== e.topPart ? ` (CAD ${e.topPart})` : ""}</dd>
        <dt>CAD System</dt><dd>{e.cadSystem ?? ""}</dd>
        <dt>Source File</dt><dd>{e.fileName ?? ""}</dd>
        <dt>Created By</dt><dd>{e.createdByName ?? ""}, {usDate(e.createdAt)}</dd>
        <dt>Status</dt><dd>{STATE_NAME[e.state] ?? label(e.state)}</dd>
      </dl>
    </div>
  );
}

/** The CAD structure as an indented list from the top assembly, with each part's BC side. */
function StructureTree({ e }: { e: EcoDetail }) {
  const partOf = new Map(e.parts.map((p) => [p.partNumber, p]));
  const kids = new Map<string, EcoDetail["lines"]>();
  for (const l of e.lines) { if (!kids.has(l.parentPart)) kids.set(l.parentPart, []); kids.get(l.parentPart)!.push(l); }
  const rows: ReactNode[] = [];
  const walk = (pn: string, depth: number, q: number | null, pos: string | null, key: string) => {
    const p = partOf.get(pn);
    rows.push(
      <tr key={key} className={depth === 0 ? "tree-top" : ""}>
        <td className="muted">{pos ?? ""}</td>
        <td className="mono" style={{ paddingLeft: 8 + depth * 18 }}>{pn}</td>
        <td>{p?.description}</td>
        <td className="r">{q === null ? "" : qty(q)}</td>
        <td className="mono">{p?.bcItemNo}{p && p.bcItemNo !== pn ? <span className="muted"> mapped</span> : ""}</td>
        <td>{p?.inBc ? <span className="status ok">In BC</span> : <span className="status info">New item</span>}</td>
        <td>{p?.isAssembly ? (p.replenishment === "prod_order" ? "Prod. Order" : "Purchase") : p?.replenishment === "prod_order" ? "Prod. Order" : "Purchase"}</td>
        <td className="r">{p?.isAssembly ? "" : usd(p?.inBc && p.bcUnitCost ? p.bcUnitCost : p?.estCost)}</td>
        <td className="r">{usd(p?.rolledCost)}</td>
      </tr>,
    );
    if (depth < 20) for (const l of kids.get(pn) ?? []) walk(l.childPart, depth + 1, l.quantity, l.position, `${key}/${l.id}`);
  };
  walk(e.topPart, 0, null, null, "top");
  return (
    <div className="table-wrap" style={{ maxHeight: 560 }}>
      <table className="dgrid compact num-grid">
        <thead><tr><th>Item</th><th>Part No.</th><th>Description</th><th className="r">Qty. per</th><th>BC Item No.</th><th>In BC</th><th>Replenishment</th><th className="r">Unit Cost</th><th className="r">Rolled-up Cost</th></tr></thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
}

/** Parts BC doesn't have yet: the item they become, or an existing item they're really the same as. */
function NewItems({ e, parts, editable, busy, save }: { e: EcoDetail; parts: EcoPart[]; editable: boolean; busy: boolean; save: (p: EcoPart, b: Record<string, unknown>) => void }) {
  const field = (p: EcoPart, k: "bcItemNo" | "description" | "uom" | "estCost", w: number, max: number) => {
    const cur = k === "estCost" ? (p.estCost ?? "") : (p[k] ?? "");
    if (!editable) return <span className={k === "bcItemNo" ? "mono" : ""}>{k === "estCost" ? usd(p.estCost) : String(cur)}</span>;
    return (
      <input key={`${p.id}-${k}-${cur}`} className="input cell" style={{ width: w }} maxLength={max} defaultValue={String(cur)} disabled={busy} aria-label={`${k} for ${p.partNumber}`} inputMode={k === "estCost" ? "decimal" : undefined}
        onBlur={(x) => { const v = x.target.value.trim(); if (v === String(cur)) return; save(p, { [k]: k === "estCost" ? (v === "" ? null : Number(v)) : v }); }} />
    );
  };
  return (
    <div className="table-wrap">
      <table className="dgrid compact num-grid">
        <thead><tr><th>CAD Part No.</th><th>BC Item No.</th><th>Description</th><th>Unit of Measure</th><th>Replenishment</th><th className="r">Est. Unit Cost</th><th>Similar Items in BC</th></tr></thead>
        <tbody>{parts.map((p) => (
          <tr key={p.id}>
            <td className="mono">{p.partNumber}{p.revision ? <span className="muted"> rev {p.revision}</span> : ""}</td>
            <td>{field(p, "bcItemNo", 110, 20)}</td>
            <td>{field(p, "description", 240, 100)}</td>
            <td>{field(p, "uom", 70, 10)}</td>
            <td>{editable ? (
              <select className="select cell" value={p.replenishment} disabled={busy} aria-label={`Replenishment for ${p.partNumber}`} onChange={(x) => save(p, { replenishment: x.target.value })}>
                <option value="prod_order">Prod. Order</option><option value="purchase">Purchase</option>
              </select>) : p.replenishment === "prod_order" ? "Prod. Order" : "Purchase"}</td>
            <td className="r">{p.isAssembly ? <span className="muted">rolled up: {usd(p.rolledCost)}</span> : field(p, "estCost", 90, 14)}</td>
            <td>{p.suggestions.length ? p.suggestions.map((s) => (
              <span key={s.number} className="sugg">
                {editable ? <button className="linkish" disabled={busy} onClick={() => save(p, { bcItemNo: s.number })} title={`Use ${s.number} instead of creating a new item`}>Use {s.number}</button> : <span className="mono">{s.number}</span>}
                <span className="muted"> {s.description}</span>
              </span>
            )) : <span className="muted">{e.matchedAt ? "None found" : ""}</span>}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}
